import React from 'react';

const PrivacyPolicy = () => {
  return (
    <div className="space-y-6 text-gray-700 font-poppins">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-rateiq-blue mb-2">PRIVACY POLICY</h1>
        <div className="text-sm text-gray-500">
          Effective Date: June 1st, 2025
        </div>
      </div>

      <div className="leading-relaxed">
        <p className="mb-6">
          At RateIQ, we respect your privacy and are committed to protecting your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our website and services (collectively, the "Services").
        </p>
      </div>

      <div>
        <h3 className="text-xl font-medium text-rateiq-blue mb-3">1. Information We Collect</h3>
        
        <div className="mb-4">
          <h4 className="text-lg font-medium text-gray-800 mb-2">a. Information You Provide</h4>
          <ul className="list-disc list-inside space-y-2 leading-relaxed ml-4">
            <li>Contact information (email addresses, phone numbers)</li>
            <li>Business information (company name, business type)</li>
            <li>Financial documents and processing statements you upload for analysis</li>
            <li>Payment processing data contained within uploaded documents</li>
            <li>Communications you send to us</li>
          </ul>
        </div>

        <div className="mb-4">
          <h4 className="text-lg font-medium text-gray-800 mb-2">b. Information Automatically Collected</h4>
          <ul className="list-disc list-inside space-y-2 leading-relaxed ml-4">
            <li>Device information (IP address, browser type, operating system)</li>
            <li>Usage data (pages visited, time spent, features used)</li>
            <li>Cookies and similar tracking technologies</li>
            <li>Log data and analytics information</li>
          </ul>
        </div>
      </div>

      <div>
        <h3 className="text-xl font-medium text-rateiq-blue mb-3">2. How We Use Your Information</h3>
        <p className="leading-relaxed mb-3">We use your information for the following purposes:</p>
        <ul className="list-disc list-inside space-y-2 leading-relaxed ml-4">
          <li>To provide and maintain our Services</li>
          <li>To analyze your processing statements and provide rate comparisons</li>
          <li>To create benchmarks and market analytics (in anonymized/aggregated form)</li>
          <li>To improve our Services and develop new features</li>
          <li>To communicate with you about our Services</li>
          <li>To send you marketing communications (with your consent)</li>
          <li>To comply with legal obligations and protect our rights</li>
          <li>For research and development purposes</li>
          <li>To monetize data through partnerships and commercial arrangements (in de-identified form)</li>
        </ul>
      </div>

      <div>
        <h3 className="text-xl font-medium text-rateiq-blue mb-3">3. Data Sharing and Disclosure</h3>
        
        <div className="mb-4">
          <h4 className="text-lg font-medium text-gray-800 mb-2">a. Third-Party Service Providers</h4>
          <p className="leading-relaxed">
            We may share your information with trusted third-party service providers who assist us in operating our Services, conducting our business, or serving our users, provided they agree to keep this information confidential.
          </p>
        </div>

        <div className="mb-4">
          <h4 className="text-lg font-medium text-gray-800 mb-2">b. Business Partners</h4>
          <p className="leading-relaxed">
            We may share anonymized, aggregated, or de-identified data with business partners for benchmarking, market research, and commercial purposes. This data cannot be used to identify you personally.
          </p>
        </div>

        <div className="mb-4">
          <h4 className="text-lg font-medium text-gray-800 mb-2">c. Legal Requirements</h4>
          <p className="leading-relaxed">
            We may disclose your information when required by law, court order, or government regulation, or when we believe disclosure is necessary to protect our rights, your safety, or the safety of others.
          </p>
        </div>

        <div>
          <h4 className="text-lg font-medium text-gray-800 mb-2">d. Business Transfers</h4>
          <p className="leading-relaxed">
            In the event of a merger, acquisition, or sale of assets, your information may be transferred to the acquiring entity.
          </p>
        </div>
      </div>

      <div>
        <h3 className="text-xl font-medium text-rateiq-blue mb-3">4. Data Security</h3>
        <p className="leading-relaxed mb-3">
          We implement appropriate security measures to protect your information, including:
        </p>
        <ul className="list-disc list-inside space-y-2 leading-relaxed ml-4">
          <li>Encryption of data in transit and at rest</li>
          <li>Access controls and authentication measures</li>
          <li>Regular security assessments and updates</li>
          <li>Employee training on data protection practices</li>
          <li>Secure data centers and infrastructure</li>
        </ul>
        <p className="leading-relaxed mt-3">
          However, no method of transmission over the Internet or electronic storage is 100% secure, and we cannot guarantee absolute security.
        </p>
      </div>

      <div>
        <h3 className="text-xl font-medium text-rateiq-blue mb-3">5. Data Retention</h3>
        <p className="leading-relaxed">
          We retain your personal information for as long as necessary to fulfill the purposes outlined in this Privacy Policy, comply with legal obligations, resolve disputes, and enforce our agreements. User Data may be retained indefinitely for business purposes, including analytics and commercial use, in accordance with our Terms of Service.
        </p>
      </div>

      <div>
        <h3 className="text-xl font-medium text-rateiq-blue mb-3">6. Your Privacy Rights</h3>
        <p className="leading-relaxed mb-3">
          Subject to applicable law, you may have the following rights regarding your personal information:
        </p>
        <ul className="list-disc list-inside space-y-2 leading-relaxed ml-4">
          <li>Access: Request access to your personal information</li>
          <li>Correction: Request correction of inaccurate information</li>
          <li>Deletion: Request deletion of your personal information (subject to our retention policies)</li>
          <li>Portability: Request a copy of your data in a portable format</li>
          <li>Objection: Object to certain processing of your information</li>
          <li>Withdraw Consent: Withdraw consent where processing is based on consent</li>
        </ul>
        <p className="leading-relaxed mt-3">
          To exercise these rights, please contact us using the information provided below. We may need to verify your identity before processing your request.
        </p>
      </div>

      <div>
        <h3 className="text-xl font-medium text-rateiq-blue mb-3">7. Cookies and Tracking Technologies</h3>
        <p className="leading-relaxed">
          We use cookies and similar tracking technologies to enhance your experience on our Services. You can manage your cookie preferences through your browser settings, though disabling certain cookies may affect the functionality of our Services.
        </p>
      </div>

      <div>
        <h3 className="text-xl font-medium text-rateiq-blue mb-3">8. International Data Transfers</h3>
        <p className="leading-relaxed">
          Your information may be transferred to and processed in countries other than your country of residence. We ensure appropriate safeguards are in place to protect your information in accordance with this Privacy Policy and applicable laws.
        </p>
      </div>

      <div>
        <h3 className="text-xl font-medium text-rateiq-blue mb-3">9. Children's Privacy</h3>
        <p className="leading-relaxed">
          Our Services are not intended for children under 18 years of age. We do not knowingly collect personal information from children under 18. If we become aware that we have collected such information, we will take steps to delete it.
        </p>
      </div>

      <div>
        <h3 className="text-xl font-medium text-rateiq-blue mb-3">10. Updates to This Privacy Policy</h3>
        <p className="leading-relaxed">
          We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new Privacy Policy on this page and updating the effective date. Your continued use of the Services after such changes constitutes your acceptance of the updated Privacy Policy.
        </p>
      </div>

      <div>
        <h3 className="text-xl font-medium text-rateiq-blue mb-3">11. Contact Information</h3>
        <p className="leading-relaxed mb-3">
          If you have any questions about this Privacy Policy or our privacy practices, please contact us at:
        </p>
        <div className="bg-gray-50 p-4 rounded-lg">
          <p className="leading-relaxed">
            <strong>RateIQ Privacy Team</strong><br />
            Email: privacy@rateiq.com<br />
          </p>
        </div>
      </div>

      <div>
        <h3 className="text-xl font-medium text-rateiq-blue mb-3">12. Compliance</h3>
        <p className="leading-relaxed">
          This Privacy Policy is designed to comply with applicable privacy laws, including the Personal Information Protection and Electronic Documents Act (PIPEDA) in Canada, the General Data Protection Regulation (GDPR) in the European Union, and other applicable privacy regulations.
        </p>
      </div>
    </div>
  );
};

export default PrivacyPolicy; 